import React from "react";
import { footer, cabinet } from "../mock";
import { quartiers } from "../quartiers";
import { Laptop, MapPin, TrendingUp, ShieldCheck } from "lucide-react";

const iconMap = { Laptop, MapPin, TrendingUp, ShieldCheck };

function Cabinet() {
  return (
    <section id="cabinet" className="bg-[#EFEAE1] py-20 lg:py-24">
      <div className="mx-auto max-w-[1320px] px-5 lg:px-8">
        <div className="grid items-center gap-12 lg:grid-cols-2 lg:gap-16">
          <div className="relative overflow-hidden rounded-3xl">
            <img src={cabinet.image} alt="Notre cabinet" className="h-[480px] w-full object-cover" />
          </div>
          <div>
            <p className="mb-3 flex items-center gap-3 text-[12px] font-semibold uppercase tracking-[0.2em] text-[#B08D57]">
              <span className="h-px w-8 bg-[#B08D57]" /> {cabinet.eyebrow}
            </p>
            <h2 className="font-serifDisplay text-[32px] leading-tight text-[#26241F] lg:text-[42px]">
              {cabinet.title}
            </h2>
            <p className="mt-4 text-[16px] leading-relaxed text-[#6e6a62]">{cabinet.text}</p>

            <div className="mt-8 grid gap-4 sm:grid-cols-2">
              {cabinet.highlights.map((h, i) => {
                const Icon = iconMap[h.icon];
                return (
                  <div
                    key={i}
                    className="rounded-2xl border border-[#e0d8c8] bg-[#FBF9F5] p-5 transition-all duration-300 hover:-translate-y-1 hover:border-[#B08D57] hover:shadow-[0_16px_36px_-22px_rgba(38,36,31,0.4)]"
                  >
                    <span className="flex h-11 w-11 items-center justify-center rounded-full bg-[#26241F] text-[#B08D57]">
                      {Icon && <Icon className="h-5 w-5" />}
                    </span>
                    <h3 className="mt-4 text-[16px] font-semibold text-[#26241F]">{h.title}</h3>
                    <p className="mt-1.5 text-[14px] leading-relaxed text-[#6e6a62]">{h.text}</p>
                  </div>
                );
              })}
            </div>

            <div className="mt-8 grid grid-cols-2 gap-5 border-t border-[#d8cfbd] pt-7 sm:grid-cols-4">
              {cabinet.stats.map((s, i) => (
                <div key={i}>
                  <div className="font-serifDisplay text-[28px] text-[#26241F]">{s.value}</div>
                  <div className="mt-1 text-[13px] leading-snug text-[#6e6a62]">{s.label}</div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export default function Footer() {
  return (
    <>
      <Cabinet />
      <footer className="bg-[#1c1a16] py-14 text-white">
        <div className="mx-auto max-w-[1320px] px-5 lg:px-8">
          <div className="grid gap-10 lg:grid-cols-[1.3fr_1fr_1fr_1fr]">
            <div>
              <div className="font-serifDisplay text-[24px]">
                <span className="text-[#B08D57]">L</span>oyaris
              </div>
              <p className="mt-3 max-w-xs text-[14px] leading-relaxed text-white/60">{footer.tagline}</p>
            </div>
            {footer.columns.map((col, i) => (
              <div key={i}>
                <h4 className="text-[13px] font-semibold uppercase tracking-[0.15em] text-[#B08D57]">{col.title}</h4>
                <ul className="mt-4 space-y-2.5">
                  {col.links.map((l) => (
                    <li key={l.label}>
                      {l.href ? (
                        <a href={l.href} data-testid={`footer-link-${l.label}`} className="text-[14px] text-white/70 transition-colors hover:text-white">{l.label}</a>
                      ) : (
                        <span className="text-[14px] text-white/70">{l.label}</span>
                      )}
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
          <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-2 text-[13px]" data-testid="footer-quartiers">
            <span className="font-semibold uppercase tracking-[0.15em] text-[#B08D57]">Nos quartiers</span>
            {quartiers.map((q) => (
              <a key={q.slug} href={`/${q.slug}`} data-testid={`footer-link-${q.slug}`} className="text-white/70 transition-colors hover:text-white">
                Gestion locative {q.short}
              </a>
            ))}
          </div>
          <div className="mt-8 flex flex-col items-center justify-between gap-3 border-t border-white/10 pt-6 text-[13px] text-white/50 sm:flex-row">
            <span>© {new Date().getFullYear()} Loyaris. Tous droits réservés.</span>
            <span className="flex gap-3">
              <a href="/mentions-legales" data-testid="footer-link-mentions" className="transition-colors hover:text-white">Mentions légales</a>
              <span>·</span>
              <a href="/confidentialite" data-testid="footer-link-confidentialite" className="transition-colors hover:text-white">Politique de confidentialité</a>
            </span>
          </div>
        </div>
      </footer>
    </>
  );
}
